import { env } from '@/app/env'
import { Authentication } from '@/lib/authentication/Authentication'
import { getTractianUserId } from '@/lib/authentication/tractian-user-context'
import {
  ARA_EDIT_CHAT_FRAME_TYPES,
  ARA_GENERATION_FRAME_TYPES,
  ARA_INTAKE_FRAME_TYPES,
  type AraEditChatEvent,
  type AraEditTarget,
  type AraGenerationEvent,
  type AraIntakeEvent
} from '@/features/ara-generation/types/ara-events'
import type { PartialGenerationBrief } from '@/features/ara-generation/types/generation-brief'
import { parseAraStream } from '@/features/ara-generation/utils/ara-sse-parser'
import { stripAraProvenance } from '@/features/ara-generation/utils/strip-ara-provenance'

export type AraCustomSectionDef = {
  key: string
  label: string
  guidance: string
}

export type AraAttachment = {
  name: string
  mime_type: string
  data: string
}

export type AraGenerateRequest = {
  assessment_id: string
  sector?: string | null
  roles: string[]
  sections?: string[] | null
  section_order?: string[] | null
  custom_sections?: AraCustomSectionDef[] | null
  style_guide_extra?: string | null
  section_guidance_extra?: Record<string, string> | null
  language?: string
  currency?: string
  /**
   * Asset-scope trim (SCP-127). Omitted/null = every asset; a list trims the
   * report's tree to these assets (sub-assemblies ride along). Serialized only
   * when non-empty so payloads against a backend without the field are unchanged.
   */
  included_asset_ids?: string[] | null
  attachments?: AraAttachment[] | null
}

export type AraEditChatMessage = {
  role: string
  content: string
  attachments?: AraAttachment[] | null
}

export type AraEditChatRequest = {
  assessment_id?: string | null
  messages: AraEditChatMessage[]
  sections: Record<string, string>
  structured_ara?: Record<string, unknown> | null
  sector?: string | null
  target?: AraEditTarget | null
  language?: string
  currency?: string
}

export type AraRenderRequest = {
  structured_ara: Record<string, unknown>
  body_html: string
}

export type AraExportPdfRequest = {
  html: string
  structured_ara?: Record<string, unknown>
  /**
   * PDF render preset: 'print' (the service default) is the dense print
   * adaptation; 'web' renders with web fidelity scaled to the page. Only
   * serialized when set — omitted, the request body is unchanged.
   */
  layout?: 'print' | 'web'
}

/**
 * `POST /ara/export/zip`: the report as a handoff bundle (Markdown, the exact
 * HTML, and the data behind every figure as JSON). `structured_ara` is the
 * pipeline dataset the report was generated from; with it the bundle also
 * carries `data/structured_ara.json` and the per-section files. Without it
 * (only the HTML is known) the bundle is built from the document alone.
 */
export type AraExportZipRequest = {
  html: string
  structured_ara?: Record<string, unknown>
  /**
   * The assessment the report belongs to. Server-side this is what attributes
   * the `export_zip` activity row and stamps the bundle's `manifest.json`; the
   * service falls back to `structured_ara.plant.id`, which an HTML-only bundle
   * (no pipeline dataset) does not carry. Omitted when unset, never sent null.
   */
  assessment_id?: string
}

export type AraPlanRequest = {
  free_prompt?: string
  assessment_id: string
  brief?: PartialGenerationBrief | null
}

export type AraPlanResponse = AraGenerateRequest

export type AraIntakeMessage = {
  role: string
  content: string
}

export type AraIntakeRequest = {
  assessment_id: string
  messages: AraIntakeMessage[]
  brief?: PartialGenerationBrief | null
  locale?: string
  bootstrap?: boolean
}

/**
 * An accepted AI edit proposal, reported to `POST /ara/edit/accept`.
 *
 * AI edits are logged exactly once — on accept, never at stream time — so that
 * the activity log records changes the user actually kept, not proposals they
 * rejected or pure Q&A. `before`/`after` are diffed server-side; `conversation`
 * is the transcript, which is why it attaches to accepted edits only.
 */
export type AraEditAcceptRequest = {
  assessment_id?: string | null
  /** Proposal shape: 'section' | 'target' | 'span' | 'chart' | 'remove'. */
  kind?: string | null
  key?: string | null
  before?: string | null
  after?: string | null
  patch?: Record<string, unknown> | null
  conversation?: AraEditChatMessage[] | null
  sector?: string | null
  plant_name?: string | null
  company_id?: string | null
}

/** A direct (non-AI) inline edit, reported to `POST /ara/edit-log`. */
export type AraEditLogRequest = {
  assessment_id?: string | null
  section_key?: string | null
  kind?: string | null
  key?: string | null
  before?: string | null
  after?: string | null
  sector?: string | null
  plant_name?: string | null
  company_id?: string | null
}

const JSON_HEADERS = { 'Content-Type': 'application/json' } as const
const KEEPALIVE_BODY_LIMIT_BYTES = 64 * 1024

/** HTML → PDF conversions are slow Playwright renders; give them room. */
const EXPORT_PDF_TIMEOUT_MS = 120_000
/** The zip is a server-side walk of the HTML, no browser render: well under the PDF budget. */
const EXPORT_ZIP_TIMEOUT_MS = 60_000

/**
 * Build the manual auth headers for a raw `fetch` SSE call. Axios interceptors
 * don't cover `fetch`, and the ARA service wants the internal Tractian ObjectId
 * (`getTractianUserId()`), not the Auth0 `sub` returned by
 * `Authentication.getUserIdHeader()`.
 */
async function authHeaders(): Promise<Record<string, string>> {
  const token = await Authentication.getAccessToken()
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`
  }
  const userId = getTractianUserId()
  if (userId) headers['x-user-id'] = userId
  return headers
}

async function sseFetch(
  url: string,
  body: unknown,
  signal?: AbortSignal
): Promise<ReadableStream<Uint8Array>> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { ...JSON_HEADERS, ...(await authHeaders()) },
    body: JSON.stringify(body),
    signal
  })
  if (!response.ok || !response.body) {
    throw new Error(`ARA request failed: ${response.status} ${response.statusText}`)
  }
  return response.body
}

/**
 * Stream ARA generation as an async generator of parsed frames
 * (`POST /ara/stream`). Yields the {@link AraGenerationEvent} union —
 * init -> node* -> section* -> role* -> done | error — parsing the SSE body with
 * the content-tolerant, termination-strict {@link parseAraStream} (ADR 012 —
 * throws when the stream ends without a terminal frame).
 */
export async function* streamAra(
  req: AraGenerateRequest,
  signal?: AbortSignal
): AsyncGenerator<AraGenerationEvent> {
  const body = await sseFetch(`${env.araUrl}/ara/stream`, req, signal)
  for await (const frame of parseAraStream(body, signal)) {
    if (isGenerationEvent(frame)) yield frame
  }
}

/**
 * Stream an ARA edit-chat session as an async generator of parsed frames
 * (`POST /ara/edit/stream`). Yields the {@link AraEditChatEvent} union —
 * text* -> edit* -> done | error.
 *
 * Sections and `target.outer_html` carry per-section provenance blobs
 * (`<script type="application/json" data-prov-index=…>`) straight from the
 * generation stream. Those blobs are ~14 KB of inline JSON and match the WAF
 * signature that 403s any POST body containing them — stripping them here is
 * the same fix #241 applied to `/ara/render`, now for the edit chat path.
 */
export async function* streamAraEdit(
  req: AraEditChatRequest,
  signal?: AbortSignal
): AsyncGenerator<AraEditChatEvent> {
  const sanitized: AraEditChatRequest = {
    ...req,
    sections: Object.fromEntries(
      Object.entries(req.sections).map(([key, html]) => [key, stripAraProvenance(html)])
    ),
    ...(req.target
      ? { target: { ...req.target, outer_html: stripAraProvenance(req.target.outer_html) } }
      : {})
  }
  const body = await sseFetch(`${env.araUrl}/ara/edit/stream`, sanitized, signal)
  for await (const frame of parseAraStream(body, signal)) {
    if (isEditChatEvent(frame)) yield frame
  }
}

/**
 * Stream an ARA intake interview turn as an async generator of parsed frames
 * (`POST /ara/intake/stream`). Yields the {@link AraIntakeEvent} union —
 * text* / text_delta* -> brief* / brief_partial* -> done | error.
 */
export async function* streamAraIntake(
  req: AraIntakeRequest,
  signal?: AbortSignal
): AsyncGenerator<AraIntakeEvent> {
  const body = await sseFetch(`${env.araUrl}/ara/intake/stream`, req, signal)
  for await (const frame of parseAraStream(body, signal)) {
    if (isIntakeEvent(frame)) yield frame
  }
}

/**
 * Render `structured_ara` + `body_html` into the full standalone ARA report
 * HTML (`POST /ara/render`). Returns the complete HTML document string.
 */
export async function renderAra(req: AraRenderRequest, signal?: AbortSignal): Promise<string> {
  const formData = new FormData()
  formData.append('structured_ara', JSON.stringify(req.structured_ara))
  formData.append(
    'body_html',
    new Blob([stripAraProvenance(req.body_html)], { type: 'text/html' }),
    'ara.html'
  )

  const response = await fetch(`${env.araUrl}/ara/render`, {
    method: 'POST',
    headers: { ...(await authHeaders()) },
    body: formData,
    signal
  })
  if (!response.ok) {
    throw new Error(`ARA render failed: ${response.status} ${response.statusText}`)
  }
  return response.text()
}

/**
 * Convert a fully-rendered HTML document into a PDF server-side
 * (`POST /ara/export/pdf`). The `html` override makes the service skip its
 * report template, so the stored document's print CSS, colors and pagination
 * survive untouched. The optional `layout` picks the server's render preset
 * ('print' default or 'web' fidelity); it is forwarded only when present, so a
 * layout-less request keeps its historical body. Returns the raw PDF bytes as
 * a Blob.
 */
export async function exportAraPdf(req: AraExportPdfRequest, signal?: AbortSignal): Promise<Blob> {
  return postForBlob(
    `${env.araUrl}/ara/export/pdf`,
    {
      html: req.html,
      structured_ara: req.structured_ara ?? {},
      ...(req.layout ? { layout: req.layout } : {})
    },
    EXPORT_PDF_TIMEOUT_MS,
    'ARA PDF export',
    signal
  )
}

/**
 * Build the handoff bundle for a rendered report (`POST /ara/export/zip`).
 * The `html` goes out as rendered, edits included, so `ara.html` and the
 * Markdown match what the reader saw. Returns the zip bytes as a Blob.
 */
export async function exportAraZip(req: AraExportZipRequest, signal?: AbortSignal): Promise<Blob> {
  return postForBlob(
    `${env.araUrl}/ara/export/zip`,
    {
      html: req.html,
      structured_ara: req.structured_ara ?? {},
      ...(req.assessment_id ? { assessment_id: req.assessment_id } : {})
    },
    EXPORT_ZIP_TIMEOUT_MS,
    'ARA ZIP export',
    signal
  )
}

/**
 * POST a JSON body and return the binary response, bounded by a client
 * timeout. Explicit timer + controller instead of AbortSignal.timeout/any:
 * identical behavior in browsers, and the timeout stays observable under test
 * fake timers (jsdom's AbortSignal.timeout uses its own timer realm).
 */
async function postForBlob(
  url: string,
  body: unknown,
  timeoutMs: number,
  label: string,
  signal?: AbortSignal
): Promise<Blob> {
  signal?.throwIfAborted()
  const abort = new AbortController()
  const timer = setTimeout(() => {
    abort.abort(new DOMException(`${label} exceeded ${timeoutMs / 1000}s`, 'TimeoutError'))
  }, timeoutMs)
  const abortFromCaller = () => {
    if (signal) abort.abort(signal.reason)
  }
  signal?.addEventListener('abort', abortFromCaller, { once: true })
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...JSON_HEADERS, ...(await authHeaders()) },
      body: JSON.stringify(body),
      signal: abort.signal
    })
    if (!response.ok) {
      throw new Error(`${label} failed: ${response.status} ${response.statusText}`)
    }
    return await response.blob()
  } finally {
    clearTimeout(timer)
    signal?.removeEventListener('abort', abortFromCaller)
  }
}

/**
 * Compile a free-form prompt into a validated ARA generation config
 * (`POST /ara/plan`). The response is ARARequest-shaped and ready to review
 * or pass straight into {@link streamAra}.
 */
export async function planAra(req: AraPlanRequest, signal?: AbortSignal): Promise<AraPlanResponse> {
  const response = await fetch(`${env.araUrl}/ara/plan`, {
    method: 'POST',
    headers: { ...JSON_HEADERS, ...(await authHeaders()) },
    body: JSON.stringify(req),
    signal
  })
  if (!response.ok) {
    throw new Error(`ARA plan failed: ${response.status} ${response.statusText}`)
  }
  return (await response.json()) as AraPlanResponse
}

/**
 * Report an activity event to a fire-and-forget ARA logging endpoint.
 *
 * Tracking must never break the thing it is tracking: a failed or unreachable
 * log call resolves instead of throwing, mirroring the service side, where
 * every insert is best-effort and swallowed (`app/services/event_log.py`). The
 * caller is in the middle of applying a user's edit — it must not unwind
 * because an audit row didn't land.
 */
async function reportActivity(path: string, body: unknown): Promise<void> {
  try {
    const payload = JSON.stringify(body)
    await fetch(`${env.araUrl}${path}`, {
      method: 'POST',
      headers: { ...JSON_HEADERS, ...(await authHeaders()) },
      body: payload,
      // Survives the tab closing right after an edit is applied. Browsers reject
      // keepalive bodies over 64 KiB, and an accepted edit carries the whole
      // conversation plus before/after HTML — so the largest edits go as a plain
      // fetch rather than being dropped.
      keepalive: new Blob([payload]).size <= KEEPALIVE_BODY_LIMIT_BYTES
    })
  } catch {
    // Intentionally silent — see above.
  }
}

/**
 * Record that an AI edit proposal was accepted (`POST /ara/edit/accept`).
 *
 * The ARA service logs AI edits only here, so without this call every edit
 * accepted in web-v2 is invisible to the activity dashboard — including the
 * edit rate, which is the headline signal it reports.
 */
export async function araEditAccept(req: AraEditAcceptRequest): Promise<void> {
  await reportActivity('/ara/edit/accept', req)
}

/**
 * Record a direct (non-AI) inline edit (`POST /ara/edit-log`). The backend
 * writes a `manual_edit` row with a blank prompt and an HTML diff.
 */
export async function araEditLog(req: AraEditLogRequest): Promise<void> {
  await reportActivity('/ara/edit-log', req)
}

const GENERATION_TYPES: ReadonlySet<string> = new Set(ARA_GENERATION_FRAME_TYPES)
const EDIT_CHAT_TYPES: ReadonlySet<string> = new Set(ARA_EDIT_CHAT_FRAME_TYPES)
const INTAKE_TYPES: ReadonlySet<string> = new Set(ARA_INTAKE_FRAME_TYPES)

function isGenerationEvent(frame: { type: string }): frame is AraGenerationEvent {
  return GENERATION_TYPES.has(frame.type)
}

function isEditChatEvent(frame: { type: string }): frame is AraEditChatEvent {
  return EDIT_CHAT_TYPES.has(frame.type)
}

function isIntakeEvent(frame: { type: string }): frame is AraIntakeEvent {
  return INTAKE_TYPES.has(frame.type)
}
